"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import type { User } from "firebase/auth";
import PromptTextBox from "@/components/prompts/PromptTextBox";
import { PROMPTAI_API } from "@/lib/promptai";
import { checkoutHref, PASS_PLANS, SINGLE_PROMPT_PLAN } from "@/lib/promptaiPlans";

type State = { kind: "locked"; user: User | null } | { kind: "checking" } | { kind: "unlocked"; content: string };

// Placeholder shapes behind the blur -- never real prompt text (the server
// doesn't send Pro text to the page at all).
const GHOST_LINES = [92, 78, 85, 64, 88, 72, 95, 58, 81, 69, 90, 62, 84, 74];

/**
 * A Pro prompt's text box. The page itself is rendered without the text;
 * for a Google-signed-in visitor this asks the PromptAI API again with
 * their Firebase ID token (the same check the app makes) and shows the text
 * when their pass or ₹9 purchase covers it. paid_only=1 makes the API
 * withhold the text unless it's paid for -- otherwise it would send the
 * app's ad-gated text (there's no ad on the web), visible in the network tab.
 */
export default function ProPromptUnlock({
  id,
  slug,
  title,
  platforms,
}: {
  id: number;
  slug: string;
  title: string;
  platforms: string[];
}) {
  const [state, setState] = useState<State>({ kind: "locked", user: null });
  const [signingIn, setSigningIn] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let unsubscribe: (() => void) | undefined;
    // Firebase loads lazily: most visitors are signed out and never need it.
    import("@/lib/platform/firebase").then(({ subscribeAuthState }) => {
      if (cancelled) return;
      unsubscribe = subscribeAuthState(async (user) => {
        if (!user || user.isAnonymous) {
          setState({ kind: "locked", user: null });
          return;
        }
        setState({ kind: "checking" });
        try {
          const token = await user.getIdToken();
          const res = await fetch(`${PROMPTAI_API}/prompts/detail.php?id=${id}&paid_only=1&track=0`, { headers: { "X-Firebase-Token": token } });
          const data = (await res.json()) as { content?: string; locked?: boolean; ad_gated?: boolean };
          if (cancelled) return;
          if (res.ok && !data.locked && !data.ad_gated && data.content) setState({ kind: "unlocked", content: data.content.replace(/\r\n/g, "\n").trim() });
          else setState({ kind: "locked", user });
        } catch {
          if (!cancelled) setState({ kind: "locked", user });
        }
      });
    });
    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [id]);

  async function signIn(selectAccount: boolean) {
    setSigningIn(true);
    try {
      const { signInWithGoogle, signOut } = await import("@/lib/platform/firebase");
      if (selectAccount) await signOut();
      await signInWithGoogle({ selectAccount });
    } catch {
      // Closed the popup -- stay locked.
    } finally {
      setSigningIn(false);
    }
  }

  if (state.kind === "unlocked") {
    return (
      <div>
        <p className="mb-3 inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-800 dark:bg-emerald-500/10 dark:text-emerald-300">
          <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="3" aria-hidden="true">
            <path d="M5 12.5l4.5 4.5L19 7.5" />
          </svg>
          Unlocked on your account
        </p>
        <PromptTextBox id={id} content={state.content} platforms={platforms} />
      </div>
    );
  }

  const prompt = { id, title, slug };
  const halfYear = PASS_PLANS.find((p) => p.id === "half_year") ?? PASS_PLANS[0];
  const signedInUser = state.kind === "locked" ? state.user : null;

  return (
    <div className="relative overflow-hidden rounded-[1.25rem] bg-[#0b0f19] text-white shadow-[0_24px_50px_-28px_rgba(15,23,42,0.8)] ring-1 ring-white/10">
      <div aria-hidden="true" className="space-y-3 px-5 py-6 blur-[6px] select-none">
        {GHOST_LINES.map((w, i) => (
          <div key={i} className="h-3 rounded-full bg-white/15" style={{ width: `${w}%` }} />
        ))}
      </div>
      <div className="absolute inset-0 bg-[radial-gradient(70%_60%_at_50%_0%,rgba(250,204,21,0.18),transparent_70%)]" aria-hidden="true" />
      <div className="absolute inset-0 flex flex-col items-center justify-center px-5 text-center">
        {state.kind === "checking" ? (
          <p className="text-sm font-medium text-white/80">Checking your unlocks…</p>
        ) : (
          <>
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-amber-300 to-yellow-500 text-amber-950 shadow-[0_10px_30px_-8px_rgba(250,204,21,0.7)]">
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
                <rect x="5" y="11" width="14" height="10" rx="2" />
                <path d="M8 11V7a4 4 0 0 1 8 0v4" />
              </svg>
            </span>
            <p className="mt-3 text-lg font-semibold">This is a Pro prompt</p>
            <p className="mt-1 max-w-sm text-sm text-white/65">
              {signedInUser
                ? `${signedInUser.email ?? "This account"} hasn't unlocked it yet.`
                : "Unlock it once for ₹9, or get every Pro prompt with a pass."}
            </p>
            <div className="mt-5 flex w-full max-w-sm flex-col gap-2 sm:flex-row">
              <Link
                href={checkoutHref("single", prompt)}
                className="flex-1 rounded-full bg-gradient-to-r from-amber-300 to-yellow-400 px-5 py-3 text-sm font-bold text-amber-950 transition hover:-translate-y-0.5"
              >
                Unlock for ₹{SINGLE_PROMPT_PLAN.price}
              </Link>
              <Link
                href={checkoutHref(halfYear.id, prompt)}
                className="flex-1 rounded-full bg-white/10 px-5 py-3 text-sm font-semibold text-white ring-1 ring-white/20 transition hover:bg-white/15"
              >
                All Pro · from ₹{PASS_PLANS[0].price}
              </Link>
            </div>
            <button
              type="button"
              onClick={() => signIn(Boolean(signedInUser))}
              disabled={signingIn}
              className="mt-4 text-xs font-medium text-white/60 underline-offset-4 hover:text-white hover:underline disabled:opacity-50"
            >
              {signedInUser ? "Bought it on another Google account? Switch account" : "Already unlocked it? Sign in with Google"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
